import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MapPin, Phone, Star, CheckCircle, Clock, ArrowRight, Calculator, Trophy, Target, Zap, TrendingUp } from 'lucide-react';
import { updateMetaTags } from '@/utils/seoCore';
import { generateHreflangLinks, updateHreflangLinks } from '@/lib/swissHreflang';
import { generateOrganizationSchema, generateServiceSchema, generateBreadcrumbSchema, injectSchema } from '@/lib/swissSchema';
import { getCityDisplayName, SwissLanguage } from '@/lib/swissI18n';

// French content for Swiss French cities
const frenchCityContent = {
  geneve: { 
    cityNickname: 'Cité Internationale',
    industries: ['Luxe', 'ONG', 'Organisations Internationales', 'Services Financiers'],
    businessProfile: 'Hub international du luxe et des ONG avec exigences de service haut de gamme'
  },
  lausanne: { 
    cityNickname: 'Ville Olympique',
    industries: ['Startups', 'EPFL Tech', 'Hospitalité', 'Innovation'],
    businessProfile: 'Ville olympique et écosystème startup autour du parc d\'innovation EPFL'
  }
};

const frenchServiceContent = {
  'video-ia': {
    titles: {
      geneve: 'Production Vidéo IA Genève | Luxe & ONG | Future Level',
      lausanne: 'Production Vidéo IA Lausanne | Startups EPFL | Future Level'
    },
    descriptions: {
      geneve: 'Production vidéo IA haut de gamme pour les marques de luxe et ONG genevoises. Excellence et discrétion garanties selon les standards suisses.',
      lausanne: 'Vidéos IA innovantes pour l\'écosystème startup de Lausanne et EPFL. Technologie de pointe pour entreprises visionnaires de Suisse romande.'
    },
    localProofs: {
      geneve: 'Références: Rolex présentation produits, UNHCR communication, Genève Tourisme, marques de luxe genevoises',
      lausanne: 'Références: EPFL Innovation Park, Lausanne Hospitality, startups tech locales, CIO Lausanne'
    },
    specials: {
      geneve: 'Genève Spécial: Vidéos conformes aux standards internationaux. Luxe et élégance suisse. Communication multilingue discrète.',
      lausanne: 'Lausanne Spécial: Pitch decks vidéo pour startups. Intégration EPFL. Suivi performance ROI et croissance.'
    }
  },
  'webdesign': {
    titles: {
      geneve: 'Web Design Genève | Sites Luxe & ONG | Future Level',
      lausanne: 'Web Design Lausanne | Sites Startups EPFL | Future Level'
    },
    descriptions: {
      geneve: 'Web design haut de gamme pour marques de luxe et organisations internationales genevoises. Élégance et fonctionnalité selon les standards suisses.',
      lausanne: 'Sites web innovants pour l\'écosystème startup de Lausanne et entreprises EPFL. Design moderne et performance optimisée.'
    },
    localProofs: {
      geneve: 'Projets: Marques de luxe genevoises, ONG internationales, services financiers, secteur horloger',
      lausanne: 'Projets: Startups EPFL, entreprises d\'innovation, secteur hospitalier lausannois, Olympic Museum'
    },
    specials: {
      geneve: 'Genève Spécial: Standards internationaux. Luxe et élégance. Sécurité renforcée pour secteur financier.',
      lausanne: 'Lausanne Spécial: Écosystème startup. Intégration EPFL. Performance optimisée pour croissance rapide.'
    }
  },
  'referencement': {
    titles: {
      geneve: 'Référencement SEO Genève | Luxe & ONG | Future Level',
      lausanne: 'Référencement SEO Lausanne | Startups EPFL | Future Level'
    },
    descriptions: {
      geneve: 'Référencement SEO haut de gamme pour marques de luxe et ONG genevoises. Excellence et discrétion dans l\'optimisation digitale.',
      lausanne: 'SEO innovant pour l\'écosystème startup de Lausanne et entreprises EPFL. Croissance organique mesurable et durable.'
    },
    localProofs: {
      geneve: 'Succès: +300% trafic marques luxe, #1 pour "ONG Genève", dominance mots-clés internationaux',
      lausanne: 'Succès: +250% trafic startups, #1 pour "innovation EPFL", tech keywords suisse romande dominés'
    },
    specials: {
      geneve: 'Genève Spécial: SEO international. Stratégies luxe. Optimisation multilingue pour secteur horloger.',
      lausanne: 'Lausanne Spécial: SEO startup. Croissance EPFL. Innovation content marketing pour tech suisse.'
    }
  },
  'google-business-profile': {
    titles: {
      geneve: 'Google Business Profile Genève | Visibilité Locale Luxe | Future Level',
      lausanne: 'Google Business Profile Lausanne | SEO Local Startups | Future Level'
    },
    descriptions: {
      geneve: 'Optimisation Google Business Profile pour marques de luxe et ONG genevoises. Visibilité locale premium avec discrétion.',
      lausanne: 'Google Business Profile pour startups lausannoises et EPFL. Croissance locale mesurable pour entreprises innovantes.'
    },
    localProofs: {
      geneve: 'Résultats: +400% requêtes locales luxe, reviews 5 étoiles pour secteur horloger, Top-3 "services Genève"',
      lausanne: 'Résultats: +300% contacts startups, leads EPFL +50%, Top-5 "innovation Lausanne" et "tech Suisse romande"'
    },
    specials: {
      geneve: 'Genève Spécial: GBP standards internationaux. Communication luxe. Services multilingues discrets.',
      lausanne: 'Lausanne Spécial: GBP écosystème startup. Features innovation. Croissance EPFL et tech suisse.'
    }
  }
};

const FrenchSwissCityServicePage = () => {
  const { service, city } = useParams();
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();

  const currentCityData = frenchCityContent[city as keyof typeof frenchCityContent];
  const currentServiceData = frenchServiceContent[service as keyof typeof frenchServiceContent];
  const cityDisplayName = getCityDisplayName('fr', city || '');

  useEffect(() => {
    if (!currentCityData || !currentServiceData) return;

    const title = currentServiceData.titles[city as keyof typeof currentServiceData.titles];
    const description = currentServiceData.descriptions[city as keyof typeof currentServiceData.descriptions];

    // Update meta tags with city-specific content
    updateMetaTags({
      title,
      description,
      canonicalUrl: `https://www.future-level.ch/fr/${service}/${city}`,
      language: 'fr',
      keywords: `${service}, ${cityDisplayName}, Suisse, ${service} agence ${cityDisplayName}`
    });

    // Update hreflang for French city page
    const hreflangLinks = generateHreflangLinks({
      currentLanguage: 'fr' as SwissLanguage,
      currentPath: `/${service}/${city}`,
      availableLanguages: ['de', 'fr', 'it', 'en'] as SwissLanguage[]
    });
    updateHreflangLinks(hreflangLinks);

    // Inject comprehensive schemas
    const orgSchema = generateOrganizationSchema({ language: 'fr' as SwissLanguage });
    const serviceSchema = generateServiceSchema({
      serviceName: title,
      serviceSlug: service || '',
      city: city || '',
      language: 'fr' as SwissLanguage,
      priceRange: 'CHF 1500-8500',
      description,
      url: `https://www.future-level.ch/fr/${service}/${city}`
    });
    const breadcrumbSchema = generateBreadcrumbSchema('fr' as SwissLanguage, service, city);

    injectSchema({
      "@context": "https://schema.org",
      "@graph": [orgSchema, serviceSchema, breadcrumbSchema]
    });
  }, [service, city, currentCityData, currentServiceData]);

  if (!currentCityData || !currentServiceData) {
    return <div>Ville ou service non trouvé</div>;
  }

  const title = currentServiceData.titles[city as keyof typeof currentServiceData.titles];
  const description = currentServiceData.descriptions[city as keyof typeof currentServiceData.descriptions];
  const localProof = currentServiceData.localProofs[city as keyof typeof currentServiceData.localProofs];
  const special = currentServiceData.specials[city as keyof typeof currentServiceData.specials];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section with City Focus */}
        <section className="relative py-24 overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-purple-600/20 text-purple-200 border-purple-500/30">
                <MapPin className="w-4 h-4 mr-2" />
                Spécialiste {cityDisplayName} • {currentCityData.cityNickname}
              </Badge>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {service?.charAt(0).toUpperCase() + service?.slice(1).replace('-', ' ')} {cityDisplayName}
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent block">
                  {city === 'geneve' && 'Excellence & Discrétion Suisse'}
                  {city === 'lausanne' && 'Innovation EPFL & Startup'}
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                {description}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white">
                  <Phone className="w-5 h-5 mr-2" />
                  Audit Gratuit {cityDisplayName}
                </Button>
                {service === 'webdesign' ? (
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-purple-500/30 text-purple-200 hover:bg-purple-900/20"
                    onClick={() => navigate('/fr/website-kosten-rechner')}
                  >
                    <Calculator className="w-5 h-5 mr-2" />
                    Calculer Coût Site Web
                  </Button>
                ) : service === 'referencement' ? (
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-purple-500/30 text-purple-200 hover:bg-purple-900/20"
                    onClick={() => navigate('/fr/seo-roi-rechner')}
                  >
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Calculer ROI SEO
                  </Button>
                ) : (
                  <Button size="lg" variant="outline" className="border-purple-500/30 text-purple-200 hover:bg-purple-900/20">
                    <Clock className="w-5 h-5 mr-2" />
                    Consultation Gratuite
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* City Specialties */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-bold text-white text-center mb-16">
                Pourquoi {cityDisplayName} ?
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardHeader>
                    <CardTitle className="text-purple-200 flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      Industries Clés
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {currentCityData.industries.map((industry, index) => (
                        <Badge key={index} variant="outline" className="border-purple-500/30 text-purple-200">
                          {industry}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardHeader>
                    <CardTitle className="text-purple-200 flex items-center gap-2">
                      <Trophy className="w-5 h-5" />
                      Spécialité {cityDisplayName}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-300 leading-relaxed">
                      {special}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Local Proof */}
              <Card className="mt-8 bg-green-950/20 border-green-800/50">
                <CardHeader>
                  <CardTitle className="text-green-200 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Succès Locaux
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-green-100 leading-relaxed">
                    {localProof}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-purple-900/50 to-pink-900/50">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Prêt à Démarrer à {cityDisplayName} ?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Contactez notre équipe pour un audit gratuit et découvrez comment nous pouvons transformer votre présence digitale.
            </p>
            <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white">
              <ArrowRight className="w-5 h-5 mr-2" />
              Commencer Maintenant
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default FrenchSwissCityServicePage;